'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'

interface ChartErrorBoundaryProps {
  children: ReactNode
  fallbackLabel?: string
}

interface ChartErrorBoundaryState {
  hasError: boolean
  errorMessage: string | null
}

// Captura erros de render em árvores de gráficos. Quando o recharts entra em
// loop ("Maximum update depth exceeded") ou um sub-chart joga `throw`, sem
// esse boundary o erro propaga até o root e mata a página inteira.
class ChartErrorBoundary extends Component<ChartErrorBoundaryProps, ChartErrorBoundaryState> {
  state: ChartErrorBoundaryState = {
    hasError: false,
    errorMessage: null
  }

  static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { hasError: true, errorMessage: error.message || 'Erro inesperado' }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ChartErrorBoundary] componentDidCatch:', error, info.componentStack)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, errorMessage: null })
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <Card sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 1 }}>
              {this.props.fallbackLabel || 'Não foi possível exibir este gráfico'}
            </Typography>
            <Typography variant='caption' color='text.disabled' sx={{ display: 'block', mb: 2 }}>
              {this.state.errorMessage}
            </Typography>
            <Button size='small' variant='outlined' onClick={this.handleRetry}>
              Tentar novamente
            </Button>
          </Box>
        </CardContent>
      </Card>
    )
  }
}

export default ChartErrorBoundary
